import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Wallet, Users, Boxes, AlertTriangle, TrendingUp, Truck, Package, Undo2, CalendarRange } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { StatCard } from '@/components/ui/StatCard';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { useUserRole } from '@/hooks/useUserRole';
import { supabase } from '@/lib/supabaseClient';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from './nav';
import { getRxkSkus } from '@/lib/rxkSkus';

interface LowStockRow {
  sku: string;
  name_en: string | null;
  qty_available: number;
  threshold: number;
}

interface ProductToday {
  sku: string;
  name: string;
  qty: number;
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

// core_get_daily_summary interprets its date range as Asia/Dhaka calendar
// days (the business's actual "today"), so "today" is computed in the same
// timezone rather than the browser's local one.
function dhakaToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dhaka', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}

// Asia/Dhaka has no DST (fixed UTC+6), so today's start/end instants can be
// built directly from the literal offset instead of a timezone library.
function dhakaTodayBounds(): { start: string; end: string } {
  const start = new Date(`${dhakaToday()}T00:00:00+06:00`);
  const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
  return { start: start.toISOString(), end: end.toISOString() };
}

// "Rx.K Booster 50ml" -> "Booster" — short enough for the Stock On Hand
// tile's one-line breakdown.
function shortStockName(name: string): string {
  return name.replace(/^Rx\.K\s+/i, '').replace(/\s*\d+ml$/i, '').trim();
}

interface DailySummary {
  orders_total: number;
  orders_value_bdt: number;
  payment_collected_bdt: number;
  cod_pending_bdt: number;
  returns_count: number;
  cancelled_count: number;
}

export default function RxkDashboard() {
  const navigate = useNavigate();
  const { user } = useUserRole();
  const [summary, setSummary] = useState<DailySummary | null>(null);
  const [productsToday, setProductsToday] = useState<ProductToday[]>([]);
  const [ordersOpen, setOrdersOpen] = useState(0);
  const [customerCount, setCustomerCount] = useState(0);
  const [stockTotal, setStockTotal] = useState(0);
  const [topStock, setTopStock] = useState<{ sku: string; name: string; qty: number }[]>([]);
  const [trend, setTrend] = useState<{ month: string; revenue: number }[]>([]);
  const [lowStock, setLowStock] = useState<LowStockRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const today = dhakaToday();
    const { start: dayStart, end: dayEnd } = dhakaTodayBounds();
    const RXK_SKUS = await getRxkSkus();
    const [summaryRes, openOrdersRes, customerRes, stockRes, lowStockRes, monthlyRes, todayOrderIdsRes, productNamesRes] = await Promise.all([
      supabase.rpc('core_get_daily_summary', { p_date_from: today, p_date_to: today, p_channel: 'rxk_web' }),
      supabase.from('core_orders').select('id', { count: 'exact', head: true }).eq('channel', 'rxk_web').in('status', ['placed', 'confirmed', 'shipped', 'dispatched']),
      supabase.from('rxk_customers').select('id', { count: 'exact', head: true }),
      supabase.from('core_stock_current_v').select('sku, name_en, qty_on_hand').in('sku', RXK_SKUS),
      supabase.from('core_low_stock_v').select('sku, name_en, qty_available, threshold').in('sku', RXK_SKUS),
      // Post-facto corrections (a manual discount, a cancellation reversal)
      // land as separate 'adjust'/'sale_reversal' ledger rows against the
      // same order, not as an edit to the original 'sale_revenue' row — so
      // all three need summing per order to get what was actually earned.
      supabase.from('core_fin_ledger').select('occurred_at, amount_local, ref_id').eq('ref_type', 'order').in('event_type', ['sale_revenue', 'sale_reversal', 'adjust']).order('occurred_at', { ascending: true }),
      supabase.from('core_orders').select('id').eq('channel', 'rxk_web').gte('placed_at', dayStart).lt('placed_at', dayEnd).neq('status', 'cancelled'),
      supabase.from('core_products').select('sku, name_en').in('sku', RXK_SKUS),
    ]);

    if (summaryRes.error) {
      console.error('[admin dashboard] daily summary failed:', summaryRes.error.message);
      setSummary(null);
    } else {
      setSummary(summaryRes.data as DailySummary);
    }
    setOrdersOpen(openOrdersRes.count ?? 0);
    setCustomerCount(customerRes.count ?? 0);
    const stockRows = (stockRes.data as { sku: string; name_en: string | null; qty_on_hand: number }[]) ?? [];
    setStockTotal(stockRows.reduce((s, r) => s + Number(r.qty_on_hand), 0));
    setLowStock((lowStockRes.data as LowStockRow[]) ?? []);

    const stockQtyBySku: Record<string, number> = {};
    const stockNameBySku: Record<string, string> = {};
    for (const r of stockRows) {
      stockQtyBySku[r.sku] = (stockQtyBySku[r.sku] ?? 0) + Number(r.qty_on_hand);
      stockNameBySku[r.sku] = r.name_en ?? r.sku;
    }
    // Top 3 by quantity would otherwise cut Gift Box (usually the lowest
    // count, since it's a smaller add-on batch, not a full skincare SKU)
    // off the list entirely — shown separately, always, right after them.
    const topProducts = Object.entries(stockQtyBySku)
      .filter(([sku]) => sku !== 'GIFT-BOX')
      .map(([sku, qty]) => ({ sku, name: stockNameBySku[sku] ?? sku, qty }))
      .sort((a, b) => b.qty - a.qty)
      .slice(0, 3);
    const giftBox = stockQtyBySku['GIFT-BOX'] !== undefined ? [{ sku: 'GIFT-BOX', name: stockNameBySku['GIFT-BOX'] ?? 'Gift Box', qty: stockQtyBySku['GIFT-BOX'] }] : [];
    setTopStock([...topProducts, ...giftBox]);

    const todayOrderIds = ((todayOrderIdsRes.data as { id: string }[]) ?? []).map((o) => o.id);
    if (todayOrderIds.length === 0) {
      setProductsToday([]);
    } else {
      const { data: itemRows } = await supabase.from('core_order_items').select('sku, qty').in('order_id', todayOrderIds);
      const qtyBySku: Record<string, number> = {};
      for (const row of (itemRows as { sku: string; qty: number }[]) ?? []) {
        qtyBySku[row.sku] = (qtyBySku[row.sku] ?? 0) + Number(row.qty);
      }
      const nameBySku: Record<string, string> = {};
      for (const p of (productNamesRes.data as { sku: string; name_en: string | null }[]) ?? []) nameBySku[p.sku] = p.name_en ?? p.sku;
      setProductsToday(
        Object.entries(qtyBySku)
          .map(([sku, qty]) => ({ sku, name: nameBySku[sku] ?? sku, qty }))
          .sort((a, b) => b.qty - a.qty),
      );
    }

    // Revenue trend needs to be scoped to rxk_web orders — fin_ledger doesn't
    // carry channel directly, so join against core_orders client-side.
    const ledgerRows = (monthlyRes.data as { occurred_at: string; amount_local: number; ref_id: string }[]) ?? [];
    if (ledgerRows.length > 0) {
      const orderIds = [...new Set(ledgerRows.map((r) => r.ref_id))];
      const { data: rxkOrderIds } = await supabase.from('core_orders').select('id').eq('channel', 'rxk_web').in('id', orderIds);
      const rxkIdSet = new Set(((rxkOrderIds as { id: string }[]) ?? []).map((r) => r.id));
      const byMonth: Record<string, number> = {};
      for (const row of ledgerRows) {
        if (!rxkIdSet.has(row.ref_id)) continue;
        const key = new Date(row.occurred_at).toLocaleDateString('en-US', { month: 'short' });
        byMonth[key] = (byMonth[key] ?? 0) + Number(row.amount_local);
      }
      setTrend(Object.entries(byMonth).map(([month, revenue]) => ({ month, revenue })));
    } else {
      setTrend([]);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const displayName = (user?.user_metadata?.display_name as string) || (user?.user_metadata?.full_name as string) || user?.email?.split('@')[0] || '';

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-5">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-[22px] font-bold text-ink tracking-tight">{greeting()}{displayName ? `, ${displayName}` : ''}</h1>
            <p className="text-[12.5px] text-ink-faint mt-0.5">
              {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} · rxk.thekseoul.com
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => navigate('/admin/daily-summary')}>
              <CalendarRange className="w-3.5 h-3.5" /> Past Days / Custom Range
            </Button>
            <Button size="sm" variant="secondary" onClick={() => navigate('/admin/inventory')}>
              <Package className="w-3.5 h-3.5" /> Inventory
            </Button>
            <Button size="sm" onClick={() => navigate('/admin/orders')}>
              <ShoppingCart className="w-3.5 h-3.5" /> Orders
            </Button>
          </div>
        </div>

        {loading ? (
          <Spinner />
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Orders Today" value={summary?.orders_total ?? 0} icon={<ShoppingCart className="w-4 h-4" />} accent="primary" footer={`৳${(summary?.orders_value_bdt ?? 0).toLocaleString()} total value`} />
              <StatCard label="Payment Collected Today" value={`৳${(summary?.payment_collected_bdt ?? 0).toLocaleString()}`} icon={<Wallet className="w-4 h-4" />} accent="success" />
              <StatCard label="COD Pending Today" value={`৳${(summary?.cod_pending_bdt ?? 0).toLocaleString()}`} icon={<Truck className="w-4 h-4" />} accent="warning" />
              <StatCard label="Returns Today" value={summary?.returns_count ?? 0} icon={<Undo2 className="w-4 h-4" />} accent="error" footer={`${summary?.cancelled_count ?? 0} order(s) cancelled`} />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Orders In Progress" value={ordersOpen} icon={<Truck className="w-4 h-4" />} accent="warning" />
              <StatCard
                label="Stock On Hand"
                value={stockTotal}
                icon={<Boxes className="w-4 h-4" />}
                accent="info"
                footer={
                  <span className="block truncate">
                    {topStock.length === 0
                      ? 'No stock recorded yet'
                      : topStock.map((s) => `${shortStockName(s.name)} ${s.qty}`).join('  ·  ')}
                  </span>
                }
              />
              <StatCard label="Registered Customers" value={customerCount} icon={<Users className="w-4 h-4" />} accent="primary" />
              <StatCard
                label="Orders Today by Product"
                value={summary?.orders_total ?? 0}
                icon={<Package className="w-4 h-4" />}
                accent="primary"
                footer={
                  productsToday.length === 0 ? (
                    'No products sold yet today'
                  ) : (
                    <div className="space-y-1">
                      {productsToday.map((p) => (
                        <div key={p.sku} className="flex items-center justify-between gap-2">
                          <span className="truncate">{p.name}</span>
                          <span className="font-semibold text-ink tabular-nums flex-shrink-0">{p.qty}</span>
                        </div>
                      ))}
                    </div>
                  )
                }
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <Card className="lg:col-span-2" hoverable>
                <CardHeader title="Revenue Trend" subtitle="Monthly, RxK channel only" icon={<TrendingUp className="w-4 h-4" />} />
                <CardBody>
                  {trend.length === 0 ? (
                    <EmptyState title="No revenue posted yet" hint="Fills in once orders start coming through." />
                  ) : (
                    <div className="h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={trend} margin={{ left: -12 }}>
                          <defs>
                            <linearGradient id="rxkRevenueFill" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#7B1113" stopOpacity={0.2} />
                              <stop offset="100%" stopColor="#7B1113" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
                          <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                          {/* Explicit headroom above the highest point — with few
                              months of data (often just one), Recharts' default
                              auto domain caps out exactly at that value, so the
                              point/line sits right on the top gridline and its
                              label instead of inside the chart area. */}
                          <YAxis domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.25)]} tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} width={56} tickFormatter={(v) => `৳${(v / 1000).toFixed(0)}k`} />
                          <Tooltip formatter={(v: number) => [`৳${v.toLocaleString()}`, 'Revenue']} contentStyle={{ borderRadius: 12, border: '1px solid #EEF2F7', fontSize: 12 }} />
                          <Area type="monotone" dataKey="revenue" stroke="#7B1113" strokeWidth={2.5} fill="url(#rxkRevenueFill)" dot={{ r: 4, fill: '#7B1113', strokeWidth: 0 }} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardBody>
              </Card>

              <Card hoverable>
                <CardHeader title="Low Stock Alerts" subtitle={`${lowStock.length} SKU(s)`} icon={<AlertTriangle className="w-4 h-4" />} />
                <CardBody className="space-y-1">
                  {lowStock.length === 0 ? (
                    <EmptyState title="Everything is well stocked" />
                  ) : (
                    lowStock.map((s) => (
                      <button
                        key={s.sku}
                        onClick={() => navigate('/admin/inventory')}
                        className="w-full flex items-center justify-between px-2 py-2 rounded-lg hover:bg-surface-muted transition-colors duration-150 text-left cursor-pointer"
                      >
                        <div className="min-w-0">
                          <p className="text-[12.5px] font-semibold text-ink truncate">{s.name_en ?? s.sku}</p>
                          <p className="text-[11px] text-ink-faint">{s.sku}</p>
                        </div>
                        <p className="text-[12.5px] font-bold text-warning flex-shrink-0 tabular-nums">{s.qty_available} / {s.threshold}</p>
                      </button>
                    ))
                  )}
                </CardBody>
              </Card>
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
